import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { AdSpacePhotoInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getAdSpacePhotoColumns } from './AdSpacePhotosColumns';
import { AdSpacePhotosUpdateDialog } from './AdSpacePhotosUpdateDialog';

const AdSpacePhotosAdminList: React.FC = () => {
    const [photos, setPhotos] = useState<AdSpacePhotoInterface[]>([]);
    const [photo, setPhoto] = useState<AdSpacePhotoInterface | null>(null);
    const [photoId, setPhotoId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [photoToDelete, setPhotoToDelete] = useState<AdSpacePhotoInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [photoToUpdate, setPhotoToUpdate] = useState<AdSpacePhotoInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all photos
    const handleGetAllPhotos = async () => {
        setError(null);
        setPhoto(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<AdSpacePhotoInterface[]>>('/adSpacePhotos');
            if (result.status && result.data) {
                setPhotos(result.data);
            } else {
                setError(result.message || 'Failed to fetch photos');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch photos');
        }
    };

    // Fetch photo by id
    const handleGetPhotoById = async () => {
        setError(null);
        setPhotos([]);
        setSuccess(null);
        if (!photoId) return setError('Please enter a Photo ID');
        try {
            const result = await apiGet<GenericResponseInterface<AdSpacePhotoInterface>>(`/adSpacePhotos/${photoId}`);
            if (result.status && result.data) {
                setPhoto(result.data);
            } else {
                setError(result.message || 'Failed to fetch photo');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch photo');
        }
    };

    // Delete photo
    const handleDeletePhoto = async () => {
        if (!photoToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/adSpacePhotos/delete/${photoToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Photo deleted successfully');
                setDeleteDialogOpen(false);
                setPhotoToDelete(null);
                handleGetAllPhotos();
            } else {
                setError(result.message || 'Failed to delete photo');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete photo');
        }
    };

    // Update photo (implement if you have an update dialog)
     const handleUpdatePhoto = async () => {
         if (!photoToUpdate || !photoToUpdate.id) return;
         setUpdateError(null);
         setSuccess(null);
         try {
             const result = await apiRequest<GenericResponseInterface<AdSpacePhotoInterface>>(
                 `/adSpacePhotos/update/${photoToUpdate.id}`,
                 'PUT',
                 photoToUpdate
             );
             if (result.status) {
                 setSuccess(result.message || 'Photo updated successfully');
                 setUpdateDialogOpen(false);
                 setPhotoToUpdate(null);
                 handleGetAllPhotos();
             } else {
                 setUpdateError(result.message || 'Failed to update photo');
             }
         } catch (err: any) {
             setUpdateError(err.message || 'Failed to update photo');
         }
     };

    const columns = getAdSpacePhotoColumns(
        setPhotoToDelete,
        setDeleteDialogOpen,
        setPhotoToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllPhotos();
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllPhotos}>Get All Photos</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetPhotoById}>Get Photo by ID</Button>
                <TextField
                    label="Photo ID"
                    value={photoId}
                    onChange={e => setPhotoId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {photos.length > 0 && (
                    <DataGrid
                        rows={photos.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {photo && (
                    <DataGrid
                        rows={[{ ...photo, id: photo.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this photo?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeletePhoto}>Delete</Button>
                </DialogActions>
            </Dialog>
            
            <AdSpacePhotosUpdateDialog
                open={updateDialogOpen}
                photoToUpdate={photoToUpdate}
                setPhotoToUpdate={setPhotoToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdatePhoto}
                updateError={updateError}
                updateSuccess={success}
            />

        </Box>
    );
};

export default AdSpacePhotosAdminList;